import type { Event, ReplayFrameEvent } from '@sentry/react';
import type { SpanJSON } from '@sentry/core';

function isCallbackUrl(value: string): boolean {
  return /\/signin-oidc\/?(?:[?#]|$)/i.test(value);
}

function scrubCallbackUrl(value: string): string {
  return value.replace(/(\/signin-oidc\/?)[?#][^\s"'<>]*/gi, '$1');
}

function scrubUrlFields(fields?: Record<string, unknown>, callback = false): void {
  if (!fields) return;
  const hasCallback = callback || Object.values(fields)
    .some(value => typeof value === 'string' && isCallbackUrl(value));
  for (const [key, value] of Object.entries(fields)) {
    if (typeof value === 'string') fields[key] = scrubCallbackUrl(value);
    if (hasCallback && ['http.query', 'http.fragment', 'url.query', 'url.fragment'].includes(key)) {
      delete fields[key];
    }
  }
}

export function scrubAuthenticationSpan(span: SpanJSON): SpanJSON {
  const callback = isCallbackUrl(span.description ?? '');
  if (span.description) span.description = scrubCallbackUrl(span.description);
  scrubUrlFields(span.data, callback);
  return span;
}

export function scrubAuthenticationRecording(event: ReplayFrameEvent): ReplayFrameEvent {
  if (event.data.tag === 'performanceSpan') {
    const span = event.data.payload;
    const callback = isCallbackUrl(span.description);
    span.description = scrubCallbackUrl(span.description);
    scrubUrlFields(span.data as Record<string, unknown> | undefined, callback);
  } else if (event.data.tag === 'breadcrumb') {
    const breadcrumb = event.data.payload;
    if (breadcrumb.message) breadcrumb.message = scrubCallbackUrl(breadcrumb.message);
    scrubUrlFields(breadcrumb.data as Record<string, unknown> | undefined);
  }
  return event;
}

// Villur og afkastamælingar fara um ólíkar Sentry-leiðir; hreinsa þarf báðar.
export function scrubAuthenticationEvent<T extends Event>(event: T): T {
  const callback = isCallbackUrl(event.request?.url ?? '') || isCallbackUrl(event.transaction ?? '');
  if (event.request) {
    if (event.request.url) event.request.url = scrubCallbackUrl(event.request.url);
    if (callback) delete event.request.query_string;
    scrubUrlFields(event.request.headers);
  }
  if (event.transaction) event.transaction = scrubCallbackUrl(event.transaction);
  scrubUrlFields(event.tags);
  scrubUrlFields(event.contexts?.trace?.data, callback);
  event.breadcrumbs?.forEach(breadcrumb => {
    if (breadcrumb.message) breadcrumb.message = scrubCallbackUrl(breadcrumb.message);
    scrubUrlFields(breadcrumb.data);
  });
  event.spans?.forEach(scrubAuthenticationSpan);
  return event;
}
